import { useEffect, useState } from 'react';
import { Drawer, Table, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import dayjs from 'dayjs';
import {
  entitlementBalancesApi,
  type EntitlementBalanceHistoryItem,
} from '../../api/entitlementBalances.api';
import {
  lessonKindLabel,
  ledgerBusinessRefLabel,
  ledgerTypeLabel,
} from './LedgerDetailDrawer';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { periodLedgerSummary } from '../../utils/period-ledger';

const { Text } = Typography;

/** 抽屉上下文：学生 + SKU（三列数字入口共用同一数据源）。 */
export interface BalanceHistoryContext {
  studentId: string;
  studentName: string;
  skuId: string;
  skuName: string;
}

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

/**
 * 课时流水明细抽屉（ADM-34，entitlement-balance-inquiry）。
 *
 * 点击行内购买剩余/赠送剩余/可用剩余任一课时数字打开：展示该学生该 SKU
 * 的全类型流水（H-3 单行双池，lessonKind 标明变动所属池），时间倒序分页；冲正行附业务原因。
 * 只读视图，无任何修改入口；只读详情按全仓约定以右侧抽屉浮出（IKQV7M 收口）。
 */
export function BalanceHistoryModal(props: {
  context: BalanceHistoryContext | null;
  onClose: () => void;
}) {
  const { context, onClose } = props;
  const [rows, setRows] = useState<EntitlementBalanceHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，与列表口径一致）（O-27）
  const { scope } = useGlobalCampusScope();

  useEffect(() => {
    if (!context) {
      setRows([]);
      setTotal(0);
      setPage(1);
      return;
    }
    void (async () => {
      setLoading(true);
      try {
        const res = await entitlementBalancesApi.listHistory({
          campusId: scope === 'ALL' ? undefined : scope,
          studentId: context.studentId,
          skuId: context.skuId,
          page,
          pageSize,
        });
        setRows(res.items);
        setTotal(res.total);
      } catch {
        message.error('加载课时流水失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [context, page, scope]);

  return (
    <Drawer
      open={context !== null}
      title={
        context
          ? `课时流水 - ${context.studentName} · ${context.skuName}`
          : '课时流水'
      }
      placement="right"
      width={880}
      onClose={onClose}
      destroyOnHidden
    >
      <Text type="secondary">
        以下为该学生该课程 SKU 的全部课时流水（时间倒序）；期限卡事件行不扣减课时，仅记录期限卡状态变化。
      </Text>
      <Table<EntitlementBalanceHistoryItem>
        style={{ marginTop: 12 }}
        size="small"
        rowKey="id"
        loading={loading}
        columns={[
          { title: '时间', dataIndex: 'createdAt', width: 130, render: (v) => formatDateTime(v) },
          {
            title: '课时类别',
            dataIndex: 'lessonKind',
            width: 80,
            render: (v) => lessonKindLabel[v as keyof typeof lessonKindLabel] ?? v,
          },
          {
            title: '类型',
            dataIndex: 'type',
            width: 90,
            render: (v) => ledgerTypeLabel[v] ?? v,
          },
          {
            title: '变动数量',
            dataIndex: 'availableChange',
            width: 90,
            // PERIOD 事件行池变动恒 0：数量列以「—」占位（口径镜像 ADM-24 账本流水列表）
            render: (v: number, row) =>
              row.lessonKind === 'PERIOD' ? '—' : v > 0 ? `+${v}` : `${v}`,
          },
          {
            title: '变动后可用',
            dataIndex: 'availableAfter',
            width: 90,
            // PERIOD 行代之以期限卡语义摘要（开卡/激活/调整/使用，period-ledger 共享派生）
            render: (v: number, row) =>
              row.lessonKind === 'PERIOD' ? periodLedgerSummary(row) : v,
          },
          {
            title: '业务来源',
            dataIndex: 'businessRefType',
            width: 150,
            render: (v, row) => (
              <>
                {ledgerBusinessRefLabel[v as keyof typeof ledgerBusinessRefLabel] ?? v}
                <Text type="secondary" style={{ marginLeft: 4 }}>
                  {row.businessRefId}
                </Text>
              </>
            ),
          },
          { title: '操作人', dataIndex: 'operatorName', width: 90, render: (v) => v ?? '-' },
          {
            title: '冲正原因',
            dataIndex: 'reason',
            render: (v: string | null, row) =>
              row.type === 'REVERSAL' ? (v ?? '-') : null,
          },
        ]}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (target) => setPage(target),
        }}
      />
    </Drawer>
  );
}
